// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Error Handling
import { ErrorBoundary } from './components/ErrorBoundary';

// Analytics
import { usePageTracking } from './hooks/usePageTracking';

// Pages
import { UserProvider } from './contexts/UserContext';
import { QuizProvider } from './contexts/QuizContext';
import { SyllabusProvider } from './contexts/SyllabusContext';
import MainLayout from './layouts/MainLayout';
import HomePage from './pages/HomePage';
import QuizPage from './pages/QuizPage';
import TeacherDashboard from './pages/TeacherDashboard';
import { CategoryBrowser } from './pages/CategoryBrowser';
import NotFound from './pages/NotFound';
import { QuizHistoryPage } from './pages/QuizHistoryPage';
import { QuizReviewPage } from './pages/QuizReviewPage';
import { AdminQuestionReview } from './pages/AdminQuestionReview';
import AdminDashboard from './pages/AdminDashboard';
import AccountPage from './pages/AccountPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

/**
 * PageTracker component to enable Google Analytics page tracking for SPA
 */
const PageTracker: React.FC = () => {
  usePageTracking();
  return null;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log errors to console in development
        console.error('App Error Boundary caught error:', error, errorInfo);

        // In production, you might want to log to an external service
        if (process.env.NODE_ENV === 'production') {
          // Analytics or error reporting service
          // logErrorToService(error, errorInfo);
        }
      }}
    >
      <Router>
        <PageTracker />
        <UserProvider>
          <SyllabusProvider>
            <QuizProvider>
              <Routes>
                {/* Standalone auth page - no MainLayout */}
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* App routes - with MainLayout */}
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="categories" element={<CategoryBrowser />} />
                  <Route path="quiz/:attemptId" element={<QuizPage />} />
                  <Route path="results/:attemptId" element={<QuizReviewPage />} />
                  <Route path="history" element={<QuizHistoryPage />} />
                  <Route path="history/:attemptId" element={<QuizReviewPage />} />
                  <Route path="quizzes" element={<CategoryBrowser />} />
                  <Route path="teacher" element={<TeacherDashboard />} />
                  <Route path="admin" element={<AdminDashboard />} />
                  <Route path="admin/questions" element={<AdminQuestionReview />} />
                  <Route path="account" element={<AccountPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </QuizProvider>
          </SyllabusProvider>
        </UserProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;